import React, { useState, useCallback } from "react";
import "./SwaggerContentRenderer.css";
import { detectContentType } from "../../utils/swaggerParser";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import SwaggerOperationRenderer from "../SwaggerOperationRenderer";
import SwaggerEntityRenderer from "../SwaggerEntityRenderer";

const SwaggerContentRenderer = ({
  content,
  type,
  data,
  swaggerData,
  createTryout,
  showTryout,
  updateTryout,
  documentationId,
  globalViewMode,
}) => {
  const [expandedEntities, setExpandedEntities] = useState({});
  const [viewMode, setViewMode] = useState({
    requestBody: null, // null means use global, 'table' or 'json' for override
    responses: {}, // Store view mode for each response code
  });

  // Toggle entity expansion
  const toggleEntityExpansion = useCallback((entityPath) => {
    setExpandedEntities((prev) => ({
      ...prev,
      [entityPath]: !prev[entityPath],
    }));
  }, []);

  // Toggle view mode for request body
  const toggleRequestBodyView = useCallback(() => {
    setViewMode((prev) => {
      // Determine current effective mode
      const currentMode = prev.requestBody || globalViewMode || "table";
      // Set to opposite of current mode
      const newMode = currentMode === "table" ? "json" : "table";
      return {
        ...prev,
        requestBody: newMode,
      };
    });
  }, [globalViewMode]);

  // Toggle view mode for response
  const toggleResponseView = useCallback(
    (responseCode) => {
      setViewMode((prev) => {
        // Determine current effective mode for this response
        const currentMode =
          prev.responses[responseCode] || globalViewMode || "table";
        // Set to opposite of current mode
        const newMode = currentMode === "table" ? "json" : "table";
        return {
          ...prev,
          responses: {
            ...prev.responses,
            [responseCode]: newMode,
          },
        };
      });
    },
    [globalViewMode]
  );

  // Handle try out button click
  const handleTryOut = useCallback(
    (endpoint) => {
      console.log("[TRYOUT BUTTON CLICK] ==== NEW TRYOUT REQUEST ====", {
        method: endpoint.method,
        path: endpoint.path,
        timestamp: new Date().toISOString(),
        hasParameters: !!endpoint.parameters,
        hasRequestBody: !!endpoint.requestBody,
        documentationId,
        hasCreateTryout: !!createTryout,
        hasShowTryout: !!showTryout,
        hasUpdateTryout: !!updateTryout,
      });

      if (!createTryout || !showTryout || !updateTryout || !documentationId) {
        console.error("[TRYOUT DEBUG] Missing required props:", {
          createTryout: !!createTryout,
          showTryout: !!showTryout,
          updateTryout: !!updateTryout,
          documentationId: !!documentationId,
        });
        return;
      }

      // Extract parameters and security from the endpoint
      const parameters = endpoint.parameters || [];
      let security = [];

      // Extract security definitions from swagger
      if (swaggerData?.components?.securitySchemes) {
        security = Object.keys(swaggerData.components.securitySchemes).map(
          (key) => {
            let result = swaggerData.components.securitySchemes[key];
            if (result.type === "oauth2") {
              result = {
                ...result,
                name: "Authorization",
                in: "header",
              };
            }
            return result;
          }
        );
      }

      const method = endpoint.method.toLowerCase();
      const path = endpoint.path;

      console.log("[TRYOUT DEBUG] 2. Prepared tryout data:", {
        method,
        path,
        parametersCount: parameters.length,
        securityCount: security.length,
        hasBody: !!endpoint.requestBody,
      });

      // Create the tryout in Redux
      console.log("[TRYOUT DEBUG] 3. Calling createTryout...");
      createTryout({
        documentationId,
        path: path,
        method: method,
        parameters: parameters,
        security: security,
        form: {
          query: {},
          header: {},
          path: {},
          body: endpoint.requestBody ? JSON.stringify({}, null, 2) : null,
        },
      });

      // Show the tryout window
      console.log("[TRYOUT DEBUG] 4. Calling showTryout...");
      showTryout({
        documentationId,
        path: path,
        method: method,
        parameters: parameters,
        security: security,
      });

      // Update tryout to ensure it's not minified
      console.log("[TRYOUT DEBUG] 5. Calling updateTryout to set formShown...");
      updateTryout({
        documentationId,
        path: path,
        method: method,
        values: {
          minified: false,
          formShown: true,
        },
      });

      console.log("[TRYOUT DEBUG] 6. handleTryOut completed");
    },
    [createTryout, showTryout, updateTryout, documentationId, swaggerData]
  );

  // Resolve $ref references to get the actual schema
  const resolveRef = useCallback(
    (ref) => {
      if (!ref || !swaggerData) return null;

      // Handle references like "#/components/schemas/EntityName"
      if (ref.startsWith("#/")) {
        const pathParts = ref.substring(2).split("/");
        let current = swaggerData;

        for (const part of pathParts) {
          if (current && current[part]) {
            current = current[part];
          } else {
            return null;
          }
        }

        return current;
      }

      return null;
    },
    [swaggerData]
  );

  // Get entity name from $ref
  const getEntityNameFromRef = (ref) => {
    if (!ref) return null;
    const parts = ref.split("/");
    return parts[parts.length - 1];
  };

  // Convert schema to JSON example
  const schemaToJsonExample = useCallback(
    (schema, depth = 0) => {
      if (depth > 10) return "..."; // Prevent infinite recursion

      if (!schema) return null;

      // Handle $ref
      if (schema.$ref) {
        const resolved = resolveRef(schema.$ref);
        if (resolved) {
          return schemaToJsonExample(resolved, depth + 1);
        }
        return `"${getEntityNameFromRef(schema.$ref)} Reference"`;
      }

      // Handle different types
      if (schema.type === "object") {
        if (!schema.properties) return {};
        const obj = {};
        for (const [key, value] of Object.entries(schema.properties)) {
          obj[key] = schemaToJsonExample(value, depth + 1);
        }
        return obj;
      }

      if (schema.type === "array") {
        if (schema.items) {
          return [schemaToJsonExample(schema.items, depth + 1)];
        }
        return [];
      }

      if (schema.type === "string") {
        return (
          schema.example ||
          (schema.format === "date"
            ? "2024-01-01"
            : schema.format === "date-time"
            ? "2024-01-01T00:00:00Z"
            : schema.format === "email"
            ? "example@email.com"
            : "string")
        );
      }

      if (schema.type === "number" || schema.type === "integer") {
        return schema.example || 0;
      }

      if (schema.type === "boolean") {
        return schema.example !== undefined ? schema.example : true;
      }

      // If no type specified, try to infer from example
      if (schema.example !== undefined) {
        return schema.example;
      }

      return null;
    },
    [resolveRef]
  );

  // Render a schema with expandable entity references
  const renderSchema = useCallback(
    (schema, path = "", depth = 0) => {
      if (!schema) return null;

      // Handle $ref
      if (schema.$ref) {
        const entityName = getEntityNameFromRef(schema.$ref);
        const entityPath = `${path}.${entityName}`;
        const isExpanded = expandedEntities[entityPath];
        const resolvedSchema = resolveRef(schema.$ref);

        return (
          <div className="schema-ref-container">
            <div
              className="schema-ref-header"
              onClick={() => toggleEntityExpansion(entityPath)}
            >
              <span className="expand-icon">{isExpanded ? "▼" : "▶"}</span>
              <span className="ref-type">$ref:</span>
              <span className="ref-entity">{entityName}</span>
            </div>
            {isExpanded && resolvedSchema && (
              <div className="schema-ref-expanded">
                {renderSchemaProperties(resolvedSchema, entityPath, depth + 1)}
              </div>
            )}
          </div>
        );
      }

      // Handle array types
      if (schema.type === "array" && schema.items) {
        return (
          <div className="schema-array">
            <div className="schema-type-label">Array of:</div>
            {renderSchema(schema.items, `${path}[0]`, depth)}
          </div>
        );
      }

      // Handle object types with properties
      if (schema.type === "object" || schema.properties) {
        return renderSchemaProperties(schema, path, depth);
      }

      // Handle simple types
      return (
        <div className="schema-simple">
          <span className="schema-type">{schema.type || "any"}</span>
          {schema.format && (
            <span className="schema-format"> ({schema.format})</span>
          )}
          {schema.example && (
            <span className="schema-example">
              {" "}
              Example: {JSON.stringify(schema.example)}
            </span>
          )}
        </div>
      );
    },
    [expandedEntities, resolveRef, toggleEntityExpansion]
  );

  // Render schema properties
  // Helper to check if schema needs full width rendering
  const schemaHasNestedContent = useCallback(
    (schema, path) => {
      // Check if it's an object with properties
      if (schema.type === "object" && schema.properties) {
        return true;
      }

      // Check if it's an expanded $ref
      if (schema.$ref) {
        const entityName = getEntityNameFromRef(schema.$ref);
        const entityPath = `${path}.${entityName}`;
        if (expandedEntities[entityPath]) {
          return true;
        }
      }

      // Check if it's an array of objects/refs that might be expanded
      if (schema.type === "array" && schema.items) {
        if (schema.items.type === "object" && schema.items.properties) {
          return true;
        }
        if (schema.items.$ref) {
          const entityName = getEntityNameFromRef(schema.items.$ref);
          const entityPath = `${path}[0].${entityName}`;
          if (expandedEntities[entityPath]) {
            return true;
          }
        }
      }

      return false;
    },
    [expandedEntities, getEntityNameFromRef]
  );

  const renderSchemaProperties = useCallback(
    (schema, path, depth) => {
      if (!schema.properties) {
        return (
          <div className="schema-object-empty">
            <span className="schema-type">object</span>
            {schema.description && (
              <div className="schema-description">{schema.description}</div>
            )}
          </div>
        );
      }

      return (
        <div
          className={`schema-properties ${
            depth > 0 ? "schema-properties-nested" : ""
          }`}
        >
          {schema.description && (
            <div className="schema-description">{schema.description}</div>
          )}
          <table className="schema-properties-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(schema.properties).map(
                ([propName, propSchema]) => {
                  const propPath = `${path}.${propName}`;
                  const needsFullWidth = schemaHasNestedContent(
                    propSchema,
                    propPath
                  );
                  const schemaContent = renderSchema(
                    propSchema,
                    propPath,
                    depth
                  );

                  return (
                    <React.Fragment key={propName}>
                      <tr>
                        <td className="prop-name">{propName}</td>
                        <td className="prop-type">
                          {!needsFullWidth && schemaContent}
                        </td>
                        <td className="prop-required">
                          {schema.required?.includes(propName) ? "Yes" : "No"}
                        </td>
                        <td className="prop-description">
                          {propSchema.description || "-"}
                        </td>
                      </tr>
                      {needsFullWidth && (
                        <tr className="nested-table-row">
                          <td colSpan="4" className="nested-table-cell">
                            {schemaContent}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }
              )}
            </tbody>
          </table>
        </div>
      );
    },
    [renderSchema, schemaHasNestedContent]
  );

  // Render based on content type
  const renderContent = () => {
    if (type === "info") {
      return renderInfoSection(data);
    } else if (type === "documentation") {
      return renderDocumentation(content);
    } else if (type === "endpoint") {
      return renderEndpoint(data);
    } else if (type === "entity") {
      return renderEntity(data);
    } else if (type === "security") {
      return renderSecurityScheme(data);
    } else if (type === "json") {
      return renderJSON(content || data);
    } else {
      return renderDocumentation(content);
    }
  };

  const renderInfoSection = (info) => {
    return (
      <div className="swagger-info-section">
        <h1>{info.title}</h1>
        {info.version && (
          <div className="api-version">Version: {info.version}</div>
        )}
        {info.description && renderDocumentation(info.description)}
        {info.contact && (
          <div className="info-contact">
            <h3>Contact</h3>
            {info.contact.name && <p>Name: {info.contact.name}</p>}
            {info.contact.email && (
              <p>
                Email:{" "}
                <a href={`mailto:${info.contact.email}`}>
                  {info.contact.email}
                </a>
              </p>
            )}
            {info.contact.url && (
              <p>
                URL:{" "}
                <a
                  href={info.contact.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {info.contact.url}
                </a>
              </p>
            )}
          </div>
        )}
        {info.license && (
          <div className="info-license">
            <h3>License</h3>
            <p>{info.license.name}</p>
            {info.license.url && (
              <a
                href={info.license.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                View License
              </a>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDocumentation = (text) => {
    if (!text) return null;

    // Always use ReactMarkdown for documentation content
    // This matches the approach in APIProductServiceTest page
    // ReactMarkdown with rehypeRaw handles both markdown and HTML tags properly
    return (
      <div className="swagger-markdown-content">
        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{text}</ReactMarkdown>
      </div>
    );
  };

  const renderEndpoint = (endpoint) => {
    // Get definitions from swaggerData
    const definitions =
      swaggerData?.definitions || swaggerData?.components?.schemas || {};

    return (
      <div className="swagger-endpoint-detail">
        <div className="endpoint-header">
          <span
            className={`method-badge method-${endpoint.method.toLowerCase()}`}
          >
            {endpoint.method}
          </span>
          <span className="endpoint-path">{endpoint.path}</span>
          <button
            className="try-out-button"
            onClick={() => handleTryOut(endpoint)}
            title="Try out this endpoint"
          >
            Try it out
          </button>
        </div>

        <SwaggerOperationRenderer
          operation={endpoint}
          definitions={definitions}
          path={endpoint.path}
          method={endpoint.method}
          showSummary={true}
          swaggerData={swaggerData}
        />
      </div>
    );
  };

  const renderEntity = (entity) => {
    // Get definitions from swaggerData
    const definitions =
      swaggerData?.definitions || swaggerData?.components?.schemas || {};

    // Prepare the entity schema object
    const entitySchema = {
      type: entity.schema?.type || entity.type || "object",
      description: entity.schema?.description || entity.description,
      required: entity.required || entity.schema?.required || [],
      properties: entity.properties || entity.schema?.properties || {},
      example: entity.schema?.example || entity.example,
      examples: entity.schema?.examples || entity.examples,
      discriminator: entity.schema?.discriminator || entity.discriminator,
      xml: entity.schema?.xml || entity.xml,
      // Include any other schema properties
      ...entity.schema,
    };

    return (
      <div className="swagger-entity-detail">
        <SwaggerEntityRenderer
          entity={entitySchema}
          entityName={entity.title || entity.name}
          definitions={definitions}
          showDescription={true}
          showTitle={true}
        />
      </div>
    );
  };

  const renderSecurityScheme = (security) => {
    return (
      <div className="swagger-security-detail">
        <h2>{security.title}</h2>

        {security.scheme && (
          <div className="security-details">
            {security.scheme.type && (
              <div className="security-type">
                <strong>Type:</strong> {security.scheme.type}
              </div>
            )}

            {security.scheme.description && (
              <div className="security-description">
                {renderDocumentation(security.scheme.description)}
              </div>
            )}

            {security.scheme.scheme && (
              <div className="security-scheme">
                <strong>Scheme:</strong> {security.scheme.scheme}
              </div>
            )}

            {security.scheme.bearerFormat && (
              <div className="security-bearer">
                <strong>Bearer Format:</strong> {security.scheme.bearerFormat}
              </div>
            )}

            {security.scheme.flows && (
              <div className="security-flows">
                <h3>OAuth2 Flows</h3>
                {renderJSON(security.scheme.flows)}
              </div>
            )}

            {security.scheme.openIdConnectUrl && (
              <div className="security-openid">
                <strong>OpenID Connect URL:</strong>
                <a
                  href={security.scheme.openIdConnectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {security.scheme.openIdConnectUrl}
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderJSON = (obj) => {
    if (!obj) return null;

    let jsonString;
    if (typeof obj === "string") {
      try {
        jsonString = JSON.stringify(JSON.parse(obj), null, 2);
      } catch {
        jsonString = obj;
      }
    } else {
      jsonString = JSON.stringify(obj, null, 2);
    }

    return (
      <div className="swagger-json-content">
        <pre>{jsonString}</pre>
      </div>
    );
  };

  return <div className="swagger-content-renderer">{renderContent()}</div>;
};

export default SwaggerContentRenderer;
